import React, { useEffect, useRef, useState } from "react";
import "./otpVerification.scss";
import logo from "../../assets/images/townOf1000Trails.svg";
import { Card, CardBody, Label } from "reactstrap";
import { useTranslation } from "react-i18next";
import HookForm from "../../components/hookForm/hookForm";
import CustomButton from "../../components/customButton/customButton";
import OTPInput from "react-otp-input";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { showToast } from "../../store/features/toast/toastSlice";
import { Controller } from "react-hook-form";
import axios from "axios";
import { RESEND_OTP } from "../../api/endPoint";
import { Helmet } from "react-helmet";

const OtpVerification: React.FC = () => {
  const { t } = useTranslation();
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const storedOTP = localStorage.getItem("OTP");
  const otpExpiryTime = parseInt(
    localStorage.getItem("OTPExpiresIn") || "0",
    10
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const storedEmail =
    typeof window !== "undefined"
      ? localStorage.getItem("forgotPasswordEmail")
      : "";
  const [digitValue, setDigitValue] = useState("");
  const [timer, setTimer] = useState(30);
  const [loading, setLoading] = useState(false);

  // Retrieve OTP and expiry time from local storage or state

  useEffect(() => {
    if (timer > 0) {
      const timerID = setTimeout(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);

      return () => {
        clearTimeout(timerID);
      };
    }
  }, [timer]);

  const onOtpChange = (value: string) => {
    setDigitValue(value);
  };

  useEffect(() => {
    if (digitValue?.length === 6) {
      submitButtonRef.current?.focus();
    }
  }, [digitValue]);

  const onSubmit = () => {
    setLoading(true);
    if (digitValue === storedOTP) {
      dispatch(
        showToast({
          show: true,
          message: "Code verified - Please reset your password!",
          type: "success",
        })
      );
      setTimeout(() => {
        setLoading(false);
        navigate("/reset-password");
      }, 2000); // 2 seconds delay
    } else {
      dispatch(
        showToast({
          show: true,
          message: "Incorrect Code",
          type: "error",
        })
      );
      setLoading(false);

      // Clear OTP input on invalid submission
      setDigitValue("");
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}${RESEND_OTP}`,
        {
          email: storedEmail,
        },
        {
          timeout: 5000, // Set timeout to 5 seconds (5000 milliseconds)
        }
      );

      // Update OTP and expiry time in local storage
      const { OTP, OTPExpiresIn } = response.data.data;
      localStorage.setItem("OTP", OTP.toString());
      localStorage.setItem("OTPExpiresIn", OTPExpiresIn.toString());

      // Reset timer
      setTimer(30);

      // Show success message
      dispatch(
        showToast({
          show: true,
          message: response.data.message || "OTP resent successfully!",
          type: "success",
        })
      );
    } catch (error: any) {
      if (error.code === "ECONNABORTED") {
        // Handle timeout error
        dispatch(
          showToast({
            show: true,
            message: "Request timed out. Please try again.",
            type: "error",
          })
        );
      } else {
        // Handle other errors
        dispatch(
          showToast({
            show: true,
            message: error.response?.data?.message || "Failed to resend OTP!",
            type: "error",
          })
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Verify OTP | Town of 1000 Trails Admin Panel</title>
        <meta
          name="description"
          content="Log in to manage in-app trails and more."
        />
      </Helmet>
      <div className="otpAuth-container">
        <div>
          <img className="pt-50 pb-30 logo" src={logo} alt="logo" />
          <Card className="overflow-hidden cardStyle" color="white">
            <CardBody className="d-flex  bg-indigo justify-content-between p-24 ">
              <div className="d-flex flex-column">
                <h4 className="text-left text-primary">{t("VERIFY_NOW")}</h4>
                <span className="title text-primary">
                  {t("ENTER_THE_VERIFICATION_CODE_TO_RESET_PASSWORD")}
                </span>
              </div>
              <div className="iconStyle">
                <i className="icon icon-shield-lock"></i>
              </div>
            </CardBody>
            <CardBody className="text-center px-24 py-32">
              <HookForm defaultForm={{}} onSubmit={onSubmit}>
                {(formMethod) => {
                  return (
                    <>
                      <div className="loginWrapper ">
                        <div className="texFeildWrapper mb-4">
                          <div className="d-flex mb-3">
                            <Label className="lable text-start">
                              {t(
                                "PLEASE_ENTER_THE_6_DIGITS_VERIFICATION_CODE_SENT_TO_YOUR_REGISTERED_EMAIL_ADDRESS"
                              )}
                            </Label>
                          </div>
                          <div className="emailDiv text-start mb-3 d-flex justify-content-between align-items-center">
                            <h5>{storedEmail}</h5>{" "}
                            <i
                              onClick={() => navigate("/forgot-password")}
                              className="me-2 icon icon-Edit"
                            ></i>
                          </div>
                          <div className="otp-form">
                            <Controller
                              name="otp"
                              rules={{
                                required: {
                                  value: true,
                                  message: "OTP is required",
                                },
                              }}
                              control={formMethod.control}
                              render={({ field: { onChange, value } }) => (
                                <OTPInput
                                  onChange={(otp) => {
                                    const sanitizedOtp = otp.replace(/\D/g, ""); // Remove non-digits
                                    onOtpChange(sanitizedOtp);
                                    onChange(sanitizedOtp);
                                  }}
                                  value={digitValue}
                                  numInputs={6}
                                  placeholder={"000000"}
                                  inputType={"tel"}
                                  containerStyle="otp-container py-3 m-b3"
                                  renderInput={(inputProps, index) => (
                                    <input
                                      {...inputProps}
                                      key={index}
                                      disabled={loading}
                                      autoFocus={index === 0}
                                      className={
                                        inputProps.value === ""
                                          ? "otp-input-none-style"
                                          : "otpInputFillStyle"
                                      }
                                      inputMode="numeric"
                                      autoComplete="one-time-code" // Enable OTP autofill
                                    />
                                  )}
                                />
                              )}
                            />
                          </div>
                          <div className="mt-3 resendCodeStyle text-start d-flex align-items-center">
                            {timer > 0 ? (
                              <span>{t("REQUEST_NEW_CODE_IN")}</span>
                            ) : (
                              <span>{t("DIDNT_RECEIVE_THE_CODE")}</span>
                            )}
                            {timer > 0 && (
                              <div className="resendCodeTimerStyle">
                                <span>{timer} s</span>
                              </div>
                            )}
                            {timer === 0 && (
                              <span
                                className={`ms-2 resendBtn ${
                                  loading ? "disabled" : ""
                                }`}
                                onClick={!loading ? handleResendOTP : undefined}
                                style={{
                                  cursor: loading ? "not-allowed" : "pointer",
                                }}
                              >
                                Resend Code
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="submitDiv">
                        <CustomButton
                          className="btn btn-danger text-white btn-lg text-center justify-content-center"
                          type="submit"
                          title={t("VERIFY")}
                          loading={loading}
                          disabled={digitValue?.length < 6}
                          ref={submitButtonRef}
                        />
                      </div>
                    </>
                  );
                }}
              </HookForm>
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  );
};

export default OtpVerification;
